/** The record a request is about, chosen by what it is (Q-09, G-Wauditor-08).
 *
 * The raise form asked for "Record id - the id in a record's address, /records/<id>": a UUID to copy
 * out of the address bar, and a button that stayed disabled with no word about why. This is the
 * replacement: type a site, a fuel or a month, pick a row, and the form holds the id.
 *
 * **The words are the server's** (`record_label`: site, carrier, period, tonnes), and so is the
 * engagement: the list holds only records the reader's engagement reaches, so the picker cannot be
 * the way an Auditor learns that a record outside it exists. This component builds no sentence of
 * its own and clamps nothing.
 *
 * **A record the form arrived with** (a receipt's "Raise a question about this record" carries the
 * id) is shown by its label, fetched by id by the form that owns it. If the server will not name it
 * (absent, or outside the engagement - one answer), the form asks to choose rather than offering an
 * id it cannot show.
 *
 * **A failed read is a failure**, not "no records match": the list hook throws, and the empty
 * sentence is only for a search that came back empty.
 */

import { useState } from "react";

import { useRecordOptions } from "../api/assurance";
import type { RecordOption } from "../api/assurance";
import shared from "./Screen.module.css";
import own from "./Assurance.module.css";

function cls(...parts: Array<string | false | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

export function RecordPicker({
  chosen,
  onChoose,
  searchEnabled,
}: {
  chosen: RecordOption | null;
  onChoose: (option: RecordOption | null) => void;
  /** False while the record the form arrived with is still being named, so the list is not read
   * for a search nobody has typed yet. */
  searchEnabled: boolean;
}) {
  const [text, setText] = useState("");
  const options = useRecordOptions(text, chosen === null && searchEnabled);

  if (chosen !== null) {
    return (
      <div className={cls(shared.field)}>
        Record
        <p className={cls(own.chosen)} data-testid="assurance-raise-chosen" data-record-id={chosen.id}>
          <span>{chosen.label}</span>
          <button
            type="button"
            className={cls(shared.fixLink)}
            onClick={() => {
              onChoose(null);
              setText("");
            }}
          >
            Choose another
          </button>
        </p>
      </div>
    );
  }

  const found = options.data ?? [];
  return (
    <div className={cls(shared.field)}>
      <label htmlFor="assurance-record-search">Record</label>
      <input
        id="assurance-record-search"
        type="search"
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder="Site, fuel or month"
        autoComplete="off"
        data-testid="assurance-raise-record"
      />
      {options.error ? (
        <p className={cls(own.help)} role="alert" data-testid="assurance-record-failed">
          The records could not be listed. Try again.
        </p>
      ) : options.isPending ? null : found.length === 0 ? (
        <p className={cls(own.help)} data-testid="assurance-record-none">
          No record matches.
        </p>
      ) : (
        <ul className={cls(own.options)} data-testid="assurance-record-options">
          {found.map((option) => (
            <li key={option.id}>
              <button
                type="button"
                className={cls(own.option)}
                data-record-id={option.id}
                onClick={() => onChoose(option)}
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
